// SolutionPage.jsx - Dynamic page component for Tier 3 solution/product pages
import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { getLandingPage, getRelatedPages } from '../../content/landingPages';
import { getPageContent } from '../../content/pageContent';
import SolutionLayout from '../../layouts/SolutionLayout';
import SEOHead from '../../components/seo/SEOHead';
import FAQSection from '../../components/seo/FAQSection';
import { StrongCTA } from '../../components/seo/CallToAction';
import {
  CheckCircle, ArrowRight, Shield, TrendingUp, Bell, Users,
  BarChart3, Zap, Clock, Target
} from 'lucide-react';

// Feature icons mapping
const ICONS = {
  shield: Shield,
  trending: TrendingUp,
  bell: Bell,
  users: Users,
  chart: BarChart3,
  zap: Zap,
  clock: Clock,
  target: Target,
  check: CheckCircle
};

export default function SolutionPage() {
  const { slug } = useParams();
  const page = getLandingPage(slug);

  // Redirect if page doesn't exist or is not Tier 3
  if (!page) {
    return <Navigate to="/learn" replace />;
  }

  if (page.tier !== 3) {
    return <Navigate to={`/learn/${slug}`} replace />;
  }

  // Get the actual content
  const content = getPageContent(slug);
  const relatedPages = getRelatedPages(page.relatedSlugs || []);

  return (
    <SolutionLayout>
      <SEOHead
        title={page.metaTitle}
        description={page.metaDescription}
        keywords={[page.keyword, 'medicare software', 'retention platform']}
        canonicalUrl={`https://proirp.com/solutions/${slug}`}
        type="website"
        faqItems={content?.faqs || []}
      />

      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#172A3A] leading-tight mb-6">
              {page.title}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {content?.heroSubtitle || page.metaDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 bg-[#FFB800] text-[#172A3A] font-bold px-8 py-4 rounded-xl hover:bg-[#e5a600] transition-colors"
              >
                Start Free Trial
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 border-2 border-gray-300 text-gray-700 font-semibold px-8 py-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                See Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      {content?.problemSection && (
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-[#172A3A] mb-6">
                {content.problemSection.title || 'The Problem'}
              </h2>
              <div
                className="prose prose-lg max-w-none text-gray-600"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.problemSection.html) }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      {content?.features && content.features.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl font-bold text-[#172A3A] mb-12 text-center">
              {content.featuresTitle || 'How Pro IRP Helps'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {content.features.map((feature, idx) => {
                const Icon = ICONS[feature.icon] || CheckCircle;
                return (
                  <div key={idx} className="bg-white p-6 rounded-xl border border-gray-200">
                    <div className="w-12 h-12 bg-[#172A3A] rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#FFB800]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#172A3A] mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      {content?.mainContent && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div
              className="prose prose-lg max-w-none prose-headings:text-[#172A3A] prose-a:text-blue-600"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.mainContent) }}
            />
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {content?.faqs && content.faqs.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <FAQSection faqs={content.faqs} />
          </div>
        </section>
      )}

      {/* Related Solutions */}
      {relatedPages.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-8">Related Solutions</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPages.slice(0, 3).map(related => (
                <Link
                  key={related.slug}
                  to={related.tier === 3 ? `/solutions/${related.slug}` : `/learn/${related.slug}`}
                  className="p-6 border border-gray-200 rounded-xl hover:border-[#FFB800] hover:shadow-lg transition-all group"
                >
                  <h3 className="font-bold text-[#172A3A] group-hover:text-[#172A3A] mb-2">
                    {related.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {related.metaDescription?.substring(0, 100)}...
                  </p>
                  <span className="text-sm font-semibold text-[#172A3A] group-hover:text-[#FFB800] flex items-center gap-1">
                    Learn more <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Strong CTA */}
      <StrongCTA />
    </SolutionLayout>
  );
}
